'use client';

import { useEffect, useMemo } from 'react';
import Link from 'next/link';
import { usePathname } from 'next/navigation';
import { useTranslations } from 'next-intl';
import { useProject, useProjects } from '@/features/project/hooks/use-projects';
import { ProjectSettingsMenu } from './ProjectSettingsMenu';
import { ProjectSwitcher } from './ProjectSwitcher';
import { useNewProjectStore } from '../model/new-project-store';
import { PanelBottom, Plus } from 'lucide-react';
import { User, Question } from '@phosphor-icons/react';
import { Button } from '@/shared/ui/shadcn/button';
import { capitalize } from '@/shared/lib/text';
import { useLastProject } from '@/shared/hooks/use-last-project';
import { cn } from '@/shared/lib/cn';
import { useJobQueueStore } from '@/features/design/job-queue';
import { HistoryActivityIcon } from '@/shared/ui/HistoryActivityIcon';
import { useSidebarStore } from '@/shared/stores/sidebar-store';
import { useAccountIssue } from '@/shared/hooks/use-account-issue';
import { useRawTokenBalance } from '@/shared/api/hooks/use-token-balance';
import { formatTokenBalance } from '@/shared/lib/utils/tokenBalanceFormatter';
import { TokenBalanceBadge } from '@/shared/ui/TokenBalanceBadge';

interface AppSidebarProps {
  /**
   * Active building on project/concept pages. On secondary pages
   * (history/help/account) it is omitted and the sidebar resolves the
   * last-opened / first building instead.
   */
  projectId?: string;
}

/**
 * Unified left sidebar for desktop. The building is the app home, so it sits at
 * the top as its own standalone entry (the former "Dashboard" item is gone),
 * separated from the History / Account / Help block below.
 *
 * Collapsing hides the whole sidebar (Apple-style) — navigation then lives in
 * the floating bottom bar — so this component only ever renders expanded.
 */
export function AppSidebar({ projectId }: AppSidebarProps) {
  const pathname = usePathname();
  const tNav = useTranslations('navigation');
  const tGrid = useTranslations('projectGrid');
  const { lastProjectId, updateLastProject, clearLastProject } =
    useLastProject();
  const toggleCollapsed = useSidebarStore((s) => s.toggleCollapsed);
  const openNewProject = useNewProjectStore((s) => s.open);

  const { data: projectsResponse } = useProjects();
  const projects = projectsResponse?.data;
  const firstProject = projects?.[0];

  // Clear a stale lastProjectId if that building no longer exists.
  useEffect(() => {
    if (
      lastProjectId &&
      projects &&
      projects.length > 0 &&
      !projects.some((p) => p.id === lastProjectId)
    ) {
      clearLastProject();
    }
  }, [lastProjectId, projects, clearLastProject]);

  // Active building: explicit prop (project pages) → last opened → first.
  const effectiveProjectId =
    projectId || lastProjectId || firstProject?.id || null;

  // Persist the active/last-opened building so secondary pages and the next
  // session resolve straight back to it.
  useEffect(() => {
    if (projectId) {
      updateLastProject(projectId);
    } else if (!lastProjectId && firstProject?.id) {
      updateLastProject(firstProject.id);
    }
  }, [projectId, lastProjectId, firstProject?.id, updateLastProject]);

  const isHistoryPage =
    pathname === '/history' || pathname.startsWith('/history/');
  const isHelpPage = pathname.startsWith('/help');
  const isAccountPage =
    pathname === '/account' || pathname.startsWith('/account/');

  const rawTokenBalance = useRawTokenBalance();

  const activeJobCount = useJobQueueStore((state) =>
    state.getProcessingJobCount()
  );
  const hasActiveJobs = activeJobCount > 0;
  const unseenCount = useJobQueueStore((state) =>
    state.getUnseenCompletedCount()
  );
  const markJobsSeen = useJobQueueStore((state) => state.markJobsSeen);

  const conceptIdFromUrl = useMemo(() => {
    const match = pathname.match(/\/concept\/([^/]+)/);
    return match?.[1] || null;
  }, [pathname]);

  useEffect(() => {
    if (isHistoryPage) {
      markJobsSeen();
    } else if (conceptIdFromUrl) {
      markJobsSeen(conceptIdFromUrl);
    }
  }, [isHistoryPage, conceptIdFromUrl, markJobsSeen]);

  const accountIssue = useAccountIssue();
  const tokensFlag = accountIssue.attentionRows?.tokens;
  const tokensIsBadge = tokensFlag === 'warning';

  const { data: projectResponse } = useProject(effectiveProjectId || '', {
    enabled: !!effectiveProjectId,
  });
  const project = projectResponse?.data;

  return (
    <div className="h-full flex flex-col">
      {/* Collapse toggle anchored top-right, mirroring Apple's macOS sidebar.
          Collapsing hides the sidebar entirely — re-expand from the bottom bar. */}
      <div className="flex-shrink-0 flex justify-end px-3 pt-4">
        <button
          onClick={toggleCollapsed}
          className="flex items-center justify-center rounded-lg p-2 text-muted-foreground hover:text-foreground hover:bg-foreground/5 transition-colors"
          aria-label={tNav('toggleSidebar')}
        >
          <PanelBottom className="h-5 w-5 flex-shrink-0" />
        </button>
      </div>

      <div className="flex-1 min-h-0 flex flex-col overflow-y-auto pt-2">
        {/* Building section — the standalone top entry (home). */}
        {effectiveProjectId ? (
          <div className="flex-shrink-0 px-3">
            <ProjectSwitcher
              currentProjectId={effectiveProjectId}
              currentProjectName={
                project ? capitalize(project.attributes.name) : undefined
              }
            />
            <ProjectSettingsMenu projectId={effectiveProjectId} />
          </div>
        ) : (
          <div className="px-4 py-8 flex flex-col items-center gap-3 text-center text-muted-foreground text-list-item-subtitle">
            <Button size="sm" onClick={openNewProject}>
              <Plus className="h-4 w-4" />
              {tGrid('createFirst')}
            </Button>
          </div>
        )}

        {/* History / Account / Help — flows right after the building section. */}
        <nav className="mt-6 py-3 space-y-1.5 px-3">
          <Link
            href="/history"
            className={cn(
              'flex items-center gap-3 rounded-lg px-3 py-2 text-list-item-subtitle transition-colors',
              isHistoryPage
                ? 'bg-foreground/10 text-primary font-medium'
                : 'text-foreground hover:bg-foreground/5'
            )}
          >
            <HistoryActivityIcon
              activeCount={activeJobCount}
              size="md"
              className="text-primary"
              isActive={isHistoryPage}
            />
            <span>{tNav('history')}</span>
            {!hasActiveJobs && unseenCount > 0 && (
              <span className="ml-auto min-w-6 flex items-center justify-center flex-shrink-0">
                <span className="bg-primary text-primary-foreground px-1.5 py-0.5 text-chip rounded-full">
                  {unseenCount}
                </span>
              </span>
            )}
          </Link>
          <Link
            href="/account"
            className={cn(
              'flex items-center gap-3 rounded-lg px-3 py-2 text-list-item-subtitle transition-colors',
              isAccountPage
                ? 'bg-foreground/10 text-primary font-medium'
                : 'text-foreground hover:bg-foreground/5'
            )}
          >
            <User
              className="h-5 w-5 flex-shrink-0 text-primary"
              weight={isAccountPage ? 'fill' : 'regular'}
            />
            <span>{tNav('account')}</span>
            <div className="ml-auto flex items-center gap-2">
              {accountIssue.severity && !tokensIsBadge && (
                <span
                  className={cn(
                    'w-2 h-2 rounded-full',
                    accountIssue.severity === 'alert'
                      ? 'bg-red-500'
                      : 'bg-amber-500'
                  )}
                />
              )}
              {rawTokenBalance !== undefined &&
                (tokensIsBadge ? (
                  <TokenBalanceBadge
                    balance={formatTokenBalance(rawTokenBalance)}
                    tokensFlag={tokensFlag}
                    className="text-chip"
                  />
                ) : (
                  <span className="rounded-full border border-border/60 px-2 py-0.5 text-chip tabular-nums text-muted-foreground">
                    {formatTokenBalance(rawTokenBalance)}
                  </span>
                ))}
            </div>
          </Link>
          <Link
            href="/help"
            className={cn(
              'flex items-center gap-3 rounded-lg px-3 py-2 text-list-item-subtitle transition-colors',
              isHelpPage
                ? 'bg-foreground/10 text-primary font-medium'
                : 'text-foreground hover:bg-foreground/5'
            )}
          >
            <Question
              className="h-5 w-5 flex-shrink-0 text-primary"
              weight={isHelpPage ? 'fill' : 'regular'}
            />
            <span>{tNav('help')}</span>
          </Link>
        </nav>
      </div>
    </div>
  );
}
